// Chunk Bank Analysis dialog. Triggered from ChunkBank toolbar or from
// ChildProfile's chunk-bank summary. Auto-picks analysis type from the
// child's communication_style; if the child has no style on file, prompts
// the therapist with GLP / MLU / Both.
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Loader2, BarChart3, Copy, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const TYPE_OPTIONS = [
  { v: 'GLP', l: 'GLP — stage analysis' },
  { v: 'MLU', l: 'MLU — analytic measures' },
  { v: 'Both', l: 'Both' },
];

export default function ChunkAnalysisDialog({ open, onOpenChange, childId, onAnalysisSaved }) {
  const [running, setRunning] = useState(false);
  const [needsSelection, setNeedsSelection] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const reset = () => {
    setRunning(false);
    setNeedsSelection(false);
    setSelectedType('');
    setResult(null);
    setCopied(false);
  };

  const run = async (overrideType) => {
    setRunning(true);
    try {
      const body = { child_id: childId };
      if (overrideType) body.analysis_type = overrideType;
      const res = await axios.post(`${API}/chunks/${childId}/analyze`, body, auth());
      if (res.data?.needs_selection) {
        setNeedsSelection(true);
        toast.message(res.data.message || 'Pick an analysis style to continue.');
        setRunning(false);
        return;
      }
      setNeedsSelection(false);
      setResult(res.data);
      onAnalysisSaved && onAnalysisSaved(res.data);
      toast.success('Analysis ready');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not analyze chunks');
    } finally {
      setRunning(false);
    }
  };

  const handleOpenChange = (next) => {
    if (next && !result && !running) {
      reset();
      // Kick off auto-analysis the first time the dialog opens.
      run();
    }
    if (!next) reset();
    onOpenChange(next);
  };

  const copySummary = async () => {
    if (!result) return;
    const n = result.narrative || {};
    const text = [
      n.headline || '',
      '',
      n.narrative || '',
      n.key_examples?.length ? '\nExamples:\n' + n.key_examples.map((e) => `• "${e}"`).join('\n') : '',
      n.suggested_directions?.length
        ? '\nSuggested directions:\n' + n.suggested_directions.map((d) => `• ${d}`).join('\n')
        : '',
    ]
      .filter(Boolean)
      .join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Copy failed');
    }
  };

  const narrative = result?.narrative || {};
  const stats = result?.stats || {};
  const glp = stats.glp || {};
  const mlu = stats.mlu || {};

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="chunk-analysis-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" /> Chunk-bank analysis
          </DialogTitle>
          <DialogDescription>
            We use the child's recent language samples + their communication style
            to produce a clinical Language Sample Analysis suitable for reports.
          </DialogDescription>
        </DialogHeader>

        {/* Need therapist to choose a style */}
        {needsSelection && !result && (
          <div className="space-y-3 py-2" data-testid="chunk-analysis-needs-selection">
            <p className="text-sm text-stone-700">
              This child doesn't have a communication style set yet. Pick the analysis
              type to use just for this run:
            </p>
            <div className="flex flex-wrap gap-2">
              {TYPE_OPTIONS.map((t) => (
                <Button
                  key={t.v}
                  variant={selectedType === t.v ? 'default' : 'outline'}
                  className="rounded-full"
                  onClick={() => setSelectedType(t.v)}
                  data-testid={`chunk-analysis-type-${t.v}`}
                >
                  {t.l}
                </Button>
              ))}
            </div>
            <div className="flex justify-end">
              <Button
                disabled={!selectedType || running}
                onClick={() => run(selectedType)}
                className="rounded-full"
                data-testid="chunk-analysis-run-with-type-btn"
              >
                {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Run analysis
              </Button>
            </div>
          </div>
        )}

        {/* Loading */}
        {running && !result && !needsSelection && (
          <div className="flex items-center justify-center py-12 text-stone-600" data-testid="chunk-analysis-loading">
            <Loader2 className="w-6 h-6 mr-2 animate-spin" /> Analyzing chunk bank…
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-4 py-2" data-testid="chunk-analysis-result">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="secondary" className="rounded-full">
                {result.analysis_type === 'Both' ? 'GLP + MLU' : result.analysis_type}
              </Badge>
              <span className="text-xs text-stone-500">
                {result.sample_size} chunks analysed
              </span>
            </div>

            {narrative.headline && (
              <p className="font-serif text-lg text-primary-900" data-testid="chunk-analysis-headline">
                {narrative.headline}
              </p>
            )}

            {narrative.narrative && (
              <div className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed">
                {narrative.narrative}
              </div>
            )}

            {/* Stats card */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.keys(glp).length > 0 && (
                <div className="p-3 rounded-2xl bg-purple-50 border border-purple-100">
                  <div className="flex items-center gap-2 mb-1 text-purple-800">
                    <BarChart3 className="w-4 h-4" />
                    <span className="text-xs uppercase tracking-wider font-medium">GLP</span>
                  </div>
                  {glp.dominant_stage && (
                    <p className="text-sm text-purple-900">Dominant stage: <strong>{glp.dominant_stage}</strong></p>
                  )}
                  {glp.stage_distribution && Object.keys(glp.stage_distribution).length > 0 && (
                    <p className="text-xs text-purple-700 mt-1">
                      Stages: {Object.entries(glp.stage_distribution).map(([k, v]) => `S${k}×${v}`).join(', ')}
                    </p>
                  )}
                </div>
              )}
              {Object.keys(mlu).length > 0 && (
                <div className="p-3 rounded-2xl bg-blue-50 border border-blue-100">
                  <div className="flex items-center gap-2 mb-1 text-blue-800">
                    <BarChart3 className="w-4 h-4" />
                    <span className="text-xs uppercase tracking-wider font-medium">MLU</span>
                  </div>
                  <p className="text-sm text-blue-900">
                    MLU: <strong>{mlu.mlu_words}</strong> · Longest: {mlu.longest_utterance_words} words
                  </p>
                  <p className="text-xs text-blue-700 mt-1">
                    {mlu.single_word_count} single · {mlu.phrase_count} phrase · {mlu.sentence_count} sentence
                  </p>
                </div>
              )}
            </div>

            {narrative.key_examples?.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wider text-stone-500 mb-1">Examples</p>
                <ul className="space-y-1">
                  {narrative.key_examples.map((e, i) => (
                    <li key={i} className="text-sm text-stone-700">• "{e}"</li>
                  ))}
                </ul>
              </div>
            )}

            {narrative.suggested_directions?.length > 0 && (
              <div className="p-3 rounded-2xl bg-amber-50 border border-amber-100">
                <p className="text-xs uppercase tracking-wider text-amber-700 font-medium mb-1">
                  Suggested directions
                </p>
                <ul className="space-y-1">
                  {narrative.suggested_directions.map((d, i) => (
                    <li key={i} className="text-sm text-amber-900">• {d}</li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-xs text-stone-500 italic">
              This analysis is saved on the child's profile and will appear inside the
              Expressive Language → Language Sample Analysis section of the next
              assessment report (when auto-import is enabled).
            </p>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => handleOpenChange(false)} className="rounded-full">
            Close
          </Button>
          {result && (
            <>
              <Button variant="outline" onClick={() => run(result.analysis_type)} className="rounded-full" data-testid="chunk-analysis-rerun-btn">
                Re-run
              </Button>
              <Button onClick={copySummary} className="rounded-full" data-testid="chunk-analysis-copy-btn">
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? 'Copied' : 'Copy summary'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
